// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useApp } from '../../store/AppContext.js';
import { INCOME_TYPES, EXPENSE_TYPES } from '../../lib/eventLog.js';
import { formatDate } from '../../lib/formatLocale.js';

export function IncomeVsExpenses() {
  const { t } = useTranslation(['reports', 'common']);
  const { state } = useApp();
  const { ledger } = state;

  const chartData = useMemo(() => {
    const now = new Date();
    const months: { ym: string; label: string; start: string; end: string }[] = [];

    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const ym = d.toISOString().slice(0, 7);
      const year = d.getFullYear();
      const month = d.getMonth();
      const start = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month + 1, 0).getDate();
      const end = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      months.push({ ym, label: formatDate(d, { month: 'short', year: '2-digit' }), start, end });
    }

    return months.map(({ label, start, end }) => {
      let income = 0;
      let expenses = 0;

      for (const tx of ledger.transactions.values()) {
        if (tx.voided) continue;
        if (tx.date < start || tx.date > end) continue;
        for (const split of tx.splits) {
          const account = ledger.accounts.get(split.accountId);
          if (!account) continue;
          if (INCOME_TYPES.has(account.type)) income += Math.abs(split.amount);
          else if (EXPENSE_TYPES.has(account.type)) expenses += split.amount;
        }
      }

      return {
        label,
        income: income / 100,
        expenses: expenses / 100,
        net: (income - expenses) / 100,
      };
    });
  }, [ledger.transactions, ledger.accounts]);

  function formatDollar(value: number) {
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }

  const totals = chartData.reduce(
    (acc, d) => ({ income: acc.income + d.income, expenses: acc.expenses + d.expenses }),
    { income: 0, expenses: 0 },
  );
  const ytdNet = totals.income - totals.expenses;

  return (
    <div className="flex-1 overflow-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-md font-semibold text-text">{t('reports:titles.incomeVsExpenses')}</h2>
        <div className="flex gap-6 text-right">
          <div>
            <p className="text-xs text-text-muted">{t('reports:incomeVsExpenses.income12mo')}</p>
            <p className="text-md font-bold font-mono text-positive">{formatDollar(totals.income)}</p>
          </div>
          <div>
            <p className="text-xs text-text-muted">{t('reports:incomeVsExpenses.expenses12mo')}</p>
            <p className="text-md font-bold font-mono text-negative">{formatDollar(totals.expenses)}</p>
          </div>
          <div>
            <p className="text-xs text-text-muted">{t('reports:incomeVsExpenses.net12mo')}</p>
            <p className={`text-md font-bold font-mono ${ytdNet >= 0 ? 'text-positive' : 'text-negative'}`}>{formatDollar(ytdNet)}</p>
          </div>
        </div>
      </div>

      <div className="bg-bg-surface border border-border p-4 mb-4">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData} margin={{ top: 8, right: 24, left: 24, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2A2D3A" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#7B7F9E' }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={formatDollar} tick={{ fontSize: 11, fill: '#7B7F9E' }} width={80} axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={{ background: '#222535', border: '1px solid #2A2D3A', borderRadius: 0 }}
              labelStyle={{ color: '#E8E9F0', fontWeight: 600 }}
              itemStyle={{ color: '#7B7F9E' }}
              formatter={(v) => [formatDollar(Number(v))]}
            />
            <Legend wrapperStyle={{ fontSize: 12, color: '#7B7F9E' }} />
            <Bar dataKey="income" fill="#3D9970" name={t('reports:sections.income')} radius={[0, 0, 0, 0]} isAnimationActive={false} />
            <Bar dataKey="expenses" fill="#C0392B" name={t('reports:sections.expenses')} radius={[0, 0, 0, 0]} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Monthly detail table */}
      <div className="bg-bg-surface border border-border print:bg-white print:border-gray-200">
        <div className="px-4 py-1.5 border-b border-border-strong bg-bg-elevated print:border-gray-300 print:bg-gray-50">
          <h3 className="text-sm font-semibold text-text-muted uppercase tracking-wide print:text-gray-700">{t('reports:sections.monthlyDetail')}</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="border-b border-border print:border-gray-200">
            <tr>
              <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4 print:text-gray-500">{t('reports:sections.month')}</th>
              <th className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4 print:text-gray-500">{t('reports:sections.income')}</th>
              <th className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4 print:text-gray-500">{t('reports:sections.expenses')}</th>
              <th className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4 print:text-gray-500">{t('reports:sections.net')}</th>
            </tr>
          </thead>
          <tbody>
            {chartData.map((d) => (
              <tr key={d.label} className="border-b border-border hover:bg-bg-elevated print:border-gray-100 print:hover:bg-transparent">
                <td className="py-1.5 px-4 text-text-muted print:text-gray-700">{d.label}</td>
                <td className="py-1.5 px-4 text-right font-mono text-positive print:text-green-700">{formatDollar(d.income)}</td>
                <td className="py-1.5 px-4 text-right font-mono text-negative print:text-red-700">{formatDollar(d.expenses)}</td>
                <td className={`py-1.5 px-4 text-right font-mono font-medium ${d.net >= 0 ? 'text-positive print:text-green-700' : 'text-negative print:text-red-700'}`}>
                  {formatDollar(d.net)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-border-strong print:border-gray-300">
            <tr>
              <td className="py-1.5 px-4 text-sm font-semibold text-text-muted print:text-gray-700">{t('reports:incomeVsExpenses.total12mo')}</td>
              <td className="py-1.5 px-4 text-right font-mono font-semibold text-positive print:text-green-700">{formatDollar(totals.income)}</td>
              <td className="py-1.5 px-4 text-right font-mono font-semibold text-negative print:text-red-700">{formatDollar(totals.expenses)}</td>
              <td className={`py-1.5 px-4 text-right font-mono font-semibold ${ytdNet >= 0 ? 'text-positive print:text-green-700' : 'text-negative print:text-red-700'}`}>{formatDollar(ytdNet)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
